import { useCallback } from 'react';
import { setIsLayoutMode } from '../store/reducers/common.slice';
import { ensureHospitalRootOrder } from '../utils/ensureHospitalRootOrder';

/**
 * Handler for LayoutController menu **Add category** (same behavior on every
 * dashboard page that uses the hospital-style grid + categories).
 *
 * Tries to load the saved **default** layout from the API so the category is
 * created in the shared template when one exists. If the document is missing
 * or the GET throws, falls back to `getCurrentLayout()` so the user still gets
 * a new category on whatever layout is already on screen.
 *
 * @param {Object} options
 * @param {string} options.pageLabel - Short name for logs (e.g. `"Hospital"`).
 * @param {() => Promise<unknown>} options.fetchLayoutByType - From `usePageLayoutFetch`.
 * @param {() => object} options.getCurrentLayout - Page’s `getCurrentLayout` / `getCurrentLayoutV3`.
 * @param {(layout: object) => object} options.patchLayoutBase - Page’s `patch*LayoutBase`.
 * @param {(raw: unknown) => object} options.migrateLayout - Page’s layout migrator.
 * @param {import('redux').Dispatch} options.dispatch
 * @param {import('@reduxjs/toolkit').ActionCreatorWithPayload<object>} options.setPageLayout - Redux action creator (e.g. `setHospitalLayout`).
 * @param {React.Dispatch<React.SetStateAction<boolean>>} options.setLayoutTypeLoading
 * @param {React.Dispatch<React.SetStateAction<string>>} options.setCurrentLayoutType
 * @param {React.Dispatch<React.SetStateAction<object>>} options.setTempLayout
 * @param {React.Dispatch<React.SetStateAction<boolean>>} options.setHasLayoutChanges
 * @param {(categoryId: string) => void} options.scrollToCategory
 */
export default function useAddCategoryFromMenu({
	pageLabel,
	fetchLayoutByType,
	getCurrentLayout,
	patchLayoutBase,
	migrateLayout,
	dispatch,
	setPageLayout,
	setLayoutTypeLoading,
	setCurrentLayoutType,
	setTempLayout,
	setHasLayoutChanges,
	scrollToCategory,
}) {
	return useCallback(async () => {
		try {
			setLayoutTypeLoading(true);

			let baseLayout = null;
			try {
				const rawLayout = await fetchLayoutByType('default');
				if (rawLayout != null && typeof rawLayout === 'object') {
					baseLayout = ensureHospitalRootOrder(patchLayoutBase(migrateLayout(rawLayout)));
					setCurrentLayoutType('default');
				}
			} catch (fetchError) {
				console.warn(
					`${pageLabel} add-category: could not load default layout; using current layout.`,
					fetchError?.message || fetchError
				);
			}

			if (!baseLayout) {
				baseLayout = ensureHospitalRootOrder(getCurrentLayout());
			}

			dispatch(setPageLayout(baseLayout));
			dispatch(setIsLayoutMode(true));

			const newCategory = {
				id: `category_${Date.now()}`,
				title: 'New Category',
				cards: [],
			};
			const updatedLayout = ensureHospitalRootOrder({
				...baseLayout,
				categories: [...(baseLayout.categories || []), newCategory],
			});

			setTempLayout(updatedLayout);
			setHasLayoutChanges(true);
			scrollToCategory(newCategory.id);
		} catch (error) {
			console.error('Error in add-category menu flow:', error);
		} finally {
			setTimeout(() => {
				setLayoutTypeLoading(false);
			}, 200);
		}
	}, [
		pageLabel,
		fetchLayoutByType,
		getCurrentLayout,
		patchLayoutBase,
		migrateLayout,
		dispatch,
		setPageLayout,
		setLayoutTypeLoading,
		setCurrentLayoutType,
		setTempLayout,
		setHasLayoutChanges,
		scrollToCategory,
	]);
}
